import { isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import ReactModal from 'react-modal';

import Button from 'components/Button';
import { Grid, Cell } from 'components/Grid';
import SearchBarModal from 'components/SearchBarModal';
import SearchPredictiveQueriesResults from 'components/SearchPredictiveQueriesResults';
import { PAGINATION_CONFIG, SEARCH_ORIGINATIONS } from 'constants/search';
import SearchResultsContainer from 'containers/SearchResultsContainer';
import { useShopifySearch } from 'hooks/useShopifySearch';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Rudderstack from 'utils/Rudderstack';
import {
  replaceHistory,
  parseQueryString,
  queryParamSearchQuery,
  navigateToUrl,
  generateSearchUrl,
} from 'utils/Url';

import SearchModalResultsHeading from './SearchModalResultsHeading';

const TRANSITION_MS = 150;

const SearchModal = ({ isOpen, origin, onClose, trackSearchNavigation }) => {
  const { searchValueState, setSearchValueState, searchResultState } = useShopifySearch({
    paginationPageSize: PAGINATION_CONFIG.ELEMENTS_PER_PAGE_SMALL,
  });

  const updateUrlReplaceHistory = updatedSearchQuery => {
    if (!isNil(replaceHistory) && updatedSearchQuery != '') {
      const currentUrlParams = parseQueryString();

      replaceHistory(
        {
          ...currentUrlParams,
          query: encodeURI(updatedSearchQuery),
        },
        null
      );
    } else {
      replaceHistory('', null);
    }
  };

  const onCloseQuickSearch = () => {
    updateUrlReplaceHistory('');
    setSearchValueState(undefined);
    onClose();
  };

  const onClearQuickSearch = () => {
    updateUrlReplaceHistory('');
    onSearchStateChange('');
  };

  const onModaAfterOpen = () => {
    const currentUrlParams = queryParamSearchQuery();
    const initialQuery = currentUrlParams ? currentUrlParams : '';

    onSearchStateChange(initialQuery);
  };

  const onSearchStateChange = updatedSearchState => {
    setSearchValueState(updatedSearchState);
    updateUrlReplaceHistory(updatedSearchState);
  };

  /**
   * Debounced on the child component where is hooked
   */
  const onChangeSearchInput = queryInput => {
    if (!isNil(queryInput)) {
      onSearchStateChange(queryInput);
    }
  };

  const onSeeMoreClick = () => {
    const searchPageUrl = generateSearchUrl(searchValueState, { origin });
    trackSearchNavigation(searchPageUrl);
    navigateToUrl(searchPageUrl);
  };

  const onSearchBarKeyDownEnter = () => {
    const searchPageUrl = generateSearchUrl(searchValueState, { origin });
    trackSearchNavigation(searchPageUrl);
    navigateToUrl(searchPageUrl);
  };

  useEffect(() => {
    if (searchResultState?.searchTotalItems > 0 && searchValueState != '') {
      Rudderstack.productsSearched({
        query: searchValueState,
        searchResultsCount: searchResultState?.searchTotalItems,
        searchSource: window.location.href,
        searchEntry: origin || SEARCH_ORIGINATIONS.QUICK_SEARCH_BAR,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultState]);

  const exposedSearchElement = document.getElementById('TopNavigationHeaderMobile__exposed-search');
  const exposedSearchPosition = exposedSearchElement
    ? exposedSearchElement.getBoundingClientRect().y
    : 100;

  return (
    <div className="SearchModal">
      <ReactModal
        className="Overlay__content"
        onRequestClose={onCloseQuickSearch}
        onAfterOpen={onModaAfterOpen}
        isOpen={isOpen}
        preventScroll
        closeTimeoutMS={TRANSITION_MS}
      >
        <Grid isCentered>
          <Cell small={{ columns: 12 }}>
            <div className="Overlay__content">
              <div className="SearchModal__search-box">
                <SearchBarModal
                  inputValue={searchValueState}
                  origin={origin}
                  onChange={onChangeSearchInput}
                  onClear={onClearQuickSearch}
                  onBackArrowClick={onCloseQuickSearch}
                  onKeyDownEnter={onSearchBarKeyDownEnter}
                />
                <div className="SearchModal__predictive_queries">
                  <SearchPredictiveQueriesResults
                    currentSearchValue={searchValueState}
                    trackSearchNavigation={trackSearchNavigation}
                    origin={origin}
                  />
                </div>
              </div>
              <div className="SearchModal__search-results">
                <SearchModalResultsHeading currentSearchValue={searchValueState} />
                <SearchResultsContainer
                  searchResults={searchResultState?.searchItemsDisplayed}
                  trackSearchNavigation={trackSearchNavigation}
                />
              </div>
              <div className="SearchModal__see-more">
                <Button isSmall onClick={onSeeMoreClick}>
                  {I18n.localize('searchBar.seeMore')}
                </Button>
              </div>
            </div>
          </Cell>
        </Grid>
      </ReactModal>

      <style jsx>
        {`
          .Overlay__content {
            display: flex;
            flex-direction: column;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            margin: 0 auto;
            min-height: calc(100vh - ${Theme.get('spacing') * 12}px);
          }
          .SearchModal__search-box,
          SearchModal__predictive_queries {
            width: 100%;
          }
          .SearchModal__search-results {
            padding: 0 ${Theme.get('spacing') * 4}px;
          }
          .SearchModal__search-box,
          .SearchModal__search-results {
            background-color: ${Theme.get('colors-black-10')};
          }
          .SearchModal__see-more {
            display: flex;
            align-items: center;
            margin-top: auto;
            padding: ${Theme.get('spacing') * 2}px 0 ${Theme.get('spacing') * 2}px 0;
            background-color: ${Theme.get('colors-black-10')};
          }
        `}
      </style>
      <style jsx global>
        {`
          .ReactModal__Overlay {
            z-index: ${Theme.get('z-index-modal')};
            max-height: 100vh;
            overflow-y: auto;
            opacity: 0;
            transform: translateY(${exposedSearchPosition}px);
            transition: all ${TRANSITION_MS}ms ease-in-out;
          }
          .ReactModal__Overlay--after-open {
            opacity: 1;
            transform: translateY(0px);
          }
          .ReactModal__Overlay--before-close {
            transform: translateX(-100%);
          }
          .SearchModal .SearchModal__spinner-container {
            padding-top: ${Theme.get('spacing') * 10}px;
            padding-bottom: ${Theme.get('spacing') * 1.5}px;
          }
          .Button {
            margin: 0 auto;
          }
          .SearchModal__see-more .Button.Button--small {
            padding: ${Theme.get('spacing') * 2}px ${Theme.get('spacing') * 3}px;
          }
        `}
      </style>
    </div>
  );
};

SearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trackSearchNavigation: PropTypes.func.isRequired,
  origin: PropTypes.string.isRequired,
};

export default SearchModal;
